import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { NovaOrb } from "../NovaOrb";

const SCENARIOS = {
  late_meeting: {
    chipLabel: "Sick kid on Thursday",
    wakeLine: "Looks like work ran late. I'll take care of it.",
    steps: [
      { kind: "day", text: "Tuesday" },
      { kind: "event", text: "Late meeting" },
      { kind: "workout", from: "20 min", to: "8 min" },
      { kind: "note", text: "Meals matched to a shorter day" },
      { kind: "day", text: "Friday", suffix: "rebalanced" },
      { kind: "metric", text: "Consistency: 96% — still on track" },
    ],
  },
  sick_kid: {
    chipLabel: "Late meeting on Tuesday",
    wakeLine: "Looks like Thursday got complicated. I'll take care of it.",
    steps: [
      { kind: "day", text: "Thursday" },
      { kind: "event", text: "Sick kid at home" },
      { kind: "workout", from: "Gym session", to: "12-min home floor" },
      { kind: "note", text: "Week reflows around it" },
      { kind: "day", text: "Saturday", suffix: "back on plan" },
      { kind: "metric", text: "Consistency: 94% — still on track" },
    ],
  },
};

const STEP_INTERVAL = 850;
const WAKE_HOLD = 1500;

const TimelineStep = ({ step, visible, isLast }) => (
  <motion.div
    initial={{ opacity: 0, y: 16 }}
    animate={visible ? { opacity: 1, y: 0 } : {}}
    transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
    className="flex gap-4 pb-6"
    data-testid="proof-timeline-step"
  >
    <div className="flex flex-col items-center">
      <span className={`w-2 h-2 rounded-full shrink-0 ${visible ? "bg-kaizen-violet" : "bg-white/10"} transition-colors duration-500`} />
      {!isLast && <span className="w-px flex-1 bg-white/10 mt-1" />}
    </div>
    <div className="text-left pt-0.5">
      {step.kind === "workout" ? (
        <p className="text-sm text-kaizen-text">
          <span className="text-kaizen-dim2 line-through mr-2">{step.from}</span>
          <span className="text-kaizen-violetSoft font-medium">{step.to}</span>
        </p>
      ) : step.kind === "metric" ? (
        <p className="font-display italic text-lg text-kaizen-violetSoft">{step.text}</p>
      ) : (
        <p className="text-sm text-kaizen-text">
          {step.text}
          {step.suffix && <span className="text-kaizen-dim"> · {step.suffix}</span>}
        </p>
      )}
    </div>
  </motion.div>
);

export const ProofSection = ({ trigger }) => {
  const [scenarioKey, setScenarioKey] = useState("late_meeting");
  const [phase, setPhase] = useState("idle"); // idle | waking | revealing | done
  const [stepIndex, setStepIndex] = useState(-1);
  const sectionRef = useRef(null);
  const timers = useRef([]);

  const clearTimers = () => {
    timers.current.forEach(clearTimeout);
    timers.current = [];
  };

  const play = (key) => {
    clearTimers();
    setScenarioKey(key);
    setStepIndex(-1);
    setPhase("waking");
    sectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });

    timers.current.push(
      setTimeout(() => {
        setPhase("revealing");
        SCENARIOS[key].steps.forEach((_, i) => {
          timers.current.push(setTimeout(() => setStepIndex(i), i * STEP_INTERVAL));
        });
        timers.current.push(
          setTimeout(() => setPhase("done"), SCENARIOS[key].steps.length * STEP_INTERVAL + 400)
        );
      }, WAKE_HOLD)
    );
  };

  useEffect(() => {
    if (trigger > 0) play("late_meeting");
    return clearTimers;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trigger]);

  const scenario = SCENARIOS[scenarioKey];
  const otherKey = scenarioKey === "late_meeting" ? "sick_kid" : "late_meeting";

  return (
    <section ref={sectionRef} className="relative min-h-screen py-24 md:py-32 flex items-center" data-testid="proof-section">
      <div
        className={`absolute inset-0 -z-10 transition-opacity duration-700 ${
          phase === "idle" ? "opacity-0" : "opacity-100"
        } bg-[radial-gradient(circle_at_50%_30%,rgba(139,93,199,0.28),transparent_65%)]`}
      />
      <div className="max-w-xl w-full mx-auto px-6 sm:px-10">
        {phase === "idle" ? (
          <div className="flex flex-col items-center text-center opacity-40" data-testid="proof-idle-state">
            <NovaOrb size={80} pulse={false} />
            <p className="text-sm text-kaizen-dim mt-4">Tap the CTA above to see her adapt.</p>
          </div>
        ) : (
          <div className="flex flex-col items-center text-center">
            <motion.div
              animate={{ scale: phase === "waking" ? 1.12 : 1 }}
              transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            >
              <NovaOrb size={110} />
            </motion.div>

            <AnimatePresence mode="wait">
              <motion.p
                key={scenario.wakeLine}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.7 }}
                className="font-display italic text-xl sm:text-2xl text-kaizen-violetSoft mt-6 max-w-sm"
                data-testid="proof-wake-line"
              >
                {scenario.wakeLine}
              </motion.p>
            </AnimatePresence>

            {phase !== "waking" && (
              <div className="w-full mt-12" data-testid="proof-timeline">
                {scenario.steps.map((step, i) => (
                  <TimelineStep key={i} step={step} visible={i <= stepIndex} isLast={i === scenario.steps.length - 1} />
                ))}
              </div>
            )}

            {phase === "done" && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.6 }}
                className="mt-10 flex flex-col items-center gap-4"
              >
                <div className="flex items-center gap-3 flex-wrap justify-center">
                  <button
                    onClick={() => play(scenarioKey)}
                    data-testid="proof-replay-button"
                    className="text-xs text-kaizen-dim underline hover:text-kaizen-violetSoft transition-colors duration-300"
                  >
                    Replay
                  </button>
                  <button
                    onClick={() => play(otherKey)}
                    data-testid="proof-alt-scenario-chip"
                    className="text-xs px-3.5 py-1.5 rounded-full border border-kaizen-violet/30 text-kaizen-violetSoft hover:bg-kaizen-violet/10 transition-colors duration-300"
                  >
                    {scenario.chipLabel}
                  </button>
                </div>
                <p className="text-sm text-kaizen-dim mt-2 max-w-sm" data-testid="proof-closing-line">
                  That was a demo. Kaizen does this with your real life →
                </p>
                <Link
                  to="/signup"
                  data-testid="proof-signup-cta"
                  className="glow-cta bg-kaizen-violet text-[#140A24] text-sm font-semibold px-6 py-3 rounded-full hover:scale-[1.02] transition-transform duration-300"
                >
                  Start your first week
                </Link>
              </motion.div>
            )}
          </div>
        )}
      </div>
    </section>
  );
};
